import { motion } from "framer-motion";
import { MessageCircle, Phone, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { WHATSAPP_LINK, PHONE_TEL_LINK, PHONE_DISPLAY, HERO_VISUAL } from "@/constants/business";
import { useDialogs } from "@/context/DialogsContext";

export default function Hero() {
  const { requestEstimate } = useDialogs();

  return (
    <section
      id="accueil"
      data-testid="hero-section"
      className="relative bg-white overflow-hidden pt-28 pb-14 md:pt-40 md:pb-20 border-b border-gray-100"
    >
      <div className="relative max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-7"
        >
          <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-navy mb-6">
            Transport, déménagement et objets volumineux au Havre et dans toute sa région
          </h1>
          <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-xl mb-9">
            Meubles, électroménager, déménagements, achats entre particuliers, manutention, groupage et longues distances. Chaque prestation est organisée selon le trajet, le volume, les accès et l'aide nécessaire.
          </p>

          <div className="flex flex-wrap items-center gap-3 mb-6">
            <Button
              data-testid="hero-cta-estimate"
              onClick={() => requestEstimate({ source: "hero-obtenir-mon-estimation" })}
              className="bg-orange hover:bg-orange-hover text-white rounded-full px-7 py-6 font-semibold transition-transform hover:-translate-y-0.5 shadow-lg shadow-orange/20"
            >
              Obtenir mon estimation <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-whatsapp"
              className="inline-flex items-center border-2 border-whatsapp/60 text-navy rounded-full px-6 py-3 font-semibold hover:bg-whatsapp hover:text-white hover:border-whatsapp transition-colors"
            >
              <MessageCircle className="w-4 h-4 mr-2 text-whatsapp" /> Envoyer sur WhatsApp
            </a>
          </div>
          <a
            href={PHONE_TEL_LINK}
            data-testid="hero-cta-call"
            className="inline-flex items-center gap-2 text-sm font-semibold text-navy/70 hover:text-navy transition-colors"
          >
            <Phone className="w-4 h-4" /> Appeler le {PHONE_DISPLAY}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative rounded-2xl overflow-hidden border border-gray-200">
            <img
              src={HERO_VISUAL}
              alt="Camion de transport Express Delivery sur les quais du Havre"
              className="w-full h-[300px] md:h-[440px] object-cover"
              loading="eager"
              fetchPriority="high"
              data-testid="hero-visual-image"
            />
            <span className="absolute bottom-3 left-3 bg-white/85 backdrop-blur-sm text-navy/80 text-[11px] font-semibold tracking-wide rounded-full px-3 py-1.5">
              Le Havre • Normandie • France
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
